/**
 * Abstract concerns (e.g. column ordering) for rendering FulfillmentInputs.
 */
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  HydratedFulfillment,
  HydratedFulfillmentWithPreparedSampleGroup,
} from 'requests/fulfillments/HydratedFulfillmentConstants';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import {SampleGroupSchema} from 'workflows/WorkflowConstants';

export function checkFulfillmentForRenderSampleGroupField(
  hydratedFulfillment: HydratedFulfillmentWithPreparedSampleGroup
): void {
  assert(
    hydratedFulfillment.requestSchema.sampleGroupSchema != null,
    'renderSampleGroupField should only be called if all fulfillments have a sample group schema',
    {fulfillmentId: hydratedFulfillment.fulfillment.id}
  );
}

export function checkFulfillmentForRenderRequestTaskField(
  hydratedFulfillment: HydratedFulfillmentWithPreparedSampleGroup
): void {
  if (hydratedFulfillment.requestTaskSchema && !hydratedFulfillment.requestTask) {
    assert(false, 'Fulfillments with a task schema must have a task', {
      fulfillmentId: hydratedFulfillment.fulfillment.id,
    });
  }
}

function renderSchemaFields(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup | HydratedFulfillment>,
  inputToSchema: (
    object: HydratedFulfillmentWithPreparedSampleGroup | HydratedFulfillment
  ) => AnonymousSchema | null,
  renderFn: (object: SchemaField, schema: AnonymousSchema) => React.ReactNode
): Array<unknown> {
  const orderedSchemas = _.chain(hydratedFulfillments)
    .map((input) => inputToSchema(input))
    .compact()
    .sortBy('id')
    .uniq('id', /* isSorted */ true)
    .value() as Array<SampleGroupSchema>;
  const fieldsLists = orderedSchemas.map((schema) => schema.fields.map((field) => renderFn(field, schema)));
  // eslint-disable-next-line no-restricted-syntax
  return _.flatten(fieldsLists);
}

/**
 * Defines the order for rendering fulfillment columns.
 */

interface FulfillmentRenderers<T> {
  renderSampleGroupField: (field: SchemaField, sampleGroupSchema: AnonymousSchema) => Array<T>;
  renderRequestID: () => T;
  renderTask: () => T;
  renderStatus: () => T;
  renderRequestField: (field: SchemaField, requestSchema: AnonymousSchema) => T;
  renderRequestTaskField: (field: SchemaField, taskSchema: AnonymousSchema) => T;
}

export function fulfillmentRow<T>(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup | HydratedFulfillment>,
  renderers: FulfillmentRenderers<T>
): Array<T> {
  const {
    renderSampleGroupField,
    renderRequestID,
    renderTask,
    renderStatus,
    renderRequestField,
    renderRequestTaskField,
  } = renderers;

  // eslint-disable-next-line no-restricted-syntax
  const shouldRenderTask = _.some(
    hydratedFulfillments,
    (fulfillment) => fulfillment.requestTaskSchema != null
  );

  return [
    // renderSampleGroupField returns a list, meaning we need to flatten once more
    // eslint-disable-next-line no-restricted-syntax
    ..._.flatten(
      renderSchemaFields(
        hydratedFulfillments,
        (input) => input.requestSchema.sampleGroupSchema,
        renderSampleGroupField
      )
    ),
    renderRequestID(),
    ...(shouldRenderTask ? [renderTask()] : []),
    renderStatus(),
    ...renderSchemaFields(
      hydratedFulfillments,
      (fulfillment) => fulfillment.requestSchema,
      renderRequestField
    ),
    ...renderSchemaFields(
      hydratedFulfillments,
      (fulfillment) => fulfillment.requestTaskSchema,
      renderRequestTaskField
    ),
  ];
}

function sampleHeadersForField(field: SchemaField): Array<string> {
  return [field.displayName, `${field.displayName} Container`, `${field.displayName} Position`];
}

/**
 * Compute headers for hydratedFulfillments to be used in tables
 *
 * @param hydratedFulfillments FulfillmentInput[]
 * @param renderCustomSampleGroupFieldHeaders - custom function for rendering a sample field's headers.
 * @returns a list of strings to be rendered as headers of a table.
 */
export function headers(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>,
  {
    renderCustomSampleGroupFieldHeaders,
  }: {renderCustomSampleGroupFieldHeaders?: (field: SchemaField) => Array<string>}
): Array<string> {
  return fulfillmentRow(hydratedFulfillments, {
    renderSampleGroupField: (field) => {
      if (typeof renderCustomSampleGroupFieldHeaders === 'function') {
        return renderCustomSampleGroupFieldHeaders(field);
      }
      return sampleHeadersForField(field);
    },
    renderRequestID: () => `${getLegacyRequestObjectName()} ID`,
    renderTask: () => 'Task',
    renderStatus: () => 'Mark for redo',
    renderRequestField: (field) => field.displayName,
    renderRequestTaskField: (field) => field.displayName,
  });
}
